import { useRef } from 'react';
import { Stack, TextField, Typography } from '@mui/material';

import clsx from 'src/utils/clsx';
import { normalizeDecimalHours } from 'src/utils/decimalHours';

import styles from './styles';

type DecimalHoursFieldProps = {
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  inputRef?: React.MutableRefObject<HTMLInputElement>;
  isError?: boolean;
  isReadOnly?: boolean;
  testId?: string;
};

const DecimalHoursField: React.FC<DecimalHoursFieldProps> = props => {
  const {
    value,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    inputRef = null,
    isError = false,
    isReadOnly = false,
    testId,
  } = props;

  const typedValueRef = useRef<string | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    typedValueRef.current = event.target.value;
    onChange(event.target.value);
  };

  const roundTypedValue = () => {
    const isTypedValue = typedValueRef.current === value;
    typedValueRef.current = null;
    if (!isTypedValue) return;

    const roundedValue = normalizeDecimalHours(value);
    if (roundedValue !== value) onChange(roundedValue);
  };

  const handleBlur = () => {
    roundTypedValue();
    onBlur?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') roundTypedValue();
    onKeyDown?.(event);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <TextField
        sx={styles.field}
        value={value}
        error={isError}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        InputProps={{
          sx: clsx(styles.input, [[styles.readOnly, isReadOnly]]),
          placeholder: '0.00',
          readOnly: isReadOnly,
          inputRef,
          inputProps: { inputMode: 'decimal', 'data-testid': testId },
        }}
      />
      <Typography variant="body1">hrs</Typography>
    </Stack>
  );
};

export default DecimalHoursField;
